/**
 * Fragment-alpha patches for a blended surface whose Godot shader sets ALPHA otherwise than
 * three's material does. An unblended surface needs none: three's `OPAQUE` define writes alpha 1.
 */
import * as THREE from 'three';
import { editedProgram } from '../shaderPatch/chunkPatch';
export const ALPHA_TEST_CHUNK = '#include <alphatest_fragment>';
export const ALPHA_HASH_CHUNK = '#include <alphahash_fragment>';
function rewriteFragment(shader, chunk, replacement) {
    editedProgram(shader, { fragment: [{ three: chunk, godot: replacement }] }, 'surface alpha');
}
/** three multiplies the map and vertex-colour alpha into `diffuseColor.a`. This resets it to `opacity`. */
const ALBEDO_ALPHA_UNREAD = {
    cacheKey: 'albedo-alpha-unread',
    onBeforeCompile: (shader) => rewriteFragment(shader, ALPHA_TEST_CHUNK, `diffuseColor.a = opacity;\n\t${ALPHA_TEST_CHUNK}`),
};
/** three keeps the cut alpha in the blend. This writes alpha 1 for each fragment both cuts keep. */
const OPAQUE_AFTER_CUT = {
    cacheKey: 'opaque-after-cut',
    onBeforeCompile: (shader) => rewriteFragment(shader, ALPHA_HASH_CHUNK, `${ALPHA_HASH_CHUNK}\n\tdiffuseColor.a = 1.0;`),
};
/**
 * Whether three sends the surface through the blend equation. `WebGLState.setMaterial` turns
 * blending off for NoBlending, and for NormalBlending without `transparent`.
 */
function isBlended(blend) {
    const blending = blend.blending ?? THREE.NormalBlending;
    if (blending === THREE.NoBlending)
        return false;
    return blending !== THREE.NormalBlending || blend.transparent === true;
}
const NO_PROPS = Object.freeze({});
const OPAQUE_AFTER_CUT_MIX_PROPS = Object.freeze({ blending: THREE.NoBlending });
const OPAQUE_AFTER_CUT_PROPS = Object.freeze({ injection: OPAQUE_AFTER_CUT });
const ALBEDO_ALPHA_UNREAD_PROPS = Object.freeze({ injection: ALBEDO_ALPHA_UNREAD });
/**
 * The props a surface needs so that three blends the alpha its Godot shader writes. A MIX blend
 * of alpha 1 overwrites, which three spells as NoBlending: no patched program.
 */
export function surfaceAlphaProps(source, blend) {
    if (!isBlended(blend))
        return NO_PROPS;
    if (source.opaqueAfterCut) {
        return (blend.blending ?? THREE.NormalBlending) === THREE.NormalBlending
            ? OPAQUE_AFTER_CUT_MIX_PROPS
            : OPAQUE_AFTER_CUT_PROPS;
    }
    return source.readsAlbedoAlpha ? NO_PROPS : ALBEDO_ALPHA_UNREAD_PROPS;
}
//# sourceMappingURL=surfaceAlphaPatch.js.map